import type { SensorSample } from './types';

/**
 * Rolling per-channel value buffers feeding the replay DSP.
 *
 * PORTED VERBATIM from capture-app/src/live/scope.ts so the replay
 * recomputes bands/traits with byte-identical logic to the live
 * recording screen. Keep the two in sync when either changes.
 */
export class Scope {
  private readonly series = new Map<string, number[]>();

  constructor(private readonly capacity: number) {}

  /** Append a value to a channel, dropping the oldest past `capacity`. */
  push(channel: string, value: number): void {
    let s = this.series.get(channel);
    if (!s) {
      s = [];
      this.series.set(channel, s);
    }
    s.push(value);
    if (s.length > this.capacity) {
      s.splice(0, s.length - this.capacity);
    }
  }

  /** Most recent values for a channel, oldest → newest. Live reference. */
  read(channel: string): number[] {
    return this.series.get(channel) ?? [];
  }

  clear(): void {
    this.series.clear();
  }
}

/**
 * A Scope with the same reads, in amortised O(1) per value. `Scope.push`
 * splices its whole buffer on every value once full — fine for minutes of
 * data, minutes of CPU for hours of it (a 2 h recording feeds ~8 M EEG
 * values and ~4 M hand-motion readings). This keeps up to 8× the capacity
 * and trims in one go; `read` returns the same last `capacity` values.
 */
export class TailScope extends Scope {
  private readonly bufs = new Map<string, number[]>();

  constructor(private readonly cap: number) {
    super(cap);
  }

  override push(channel: string, value: number): void {
    let b = this.bufs.get(channel);
    if (!b) {
      b = [];
      this.bufs.set(channel, b);
    }
    b.push(value);
    if (b.length >= this.cap * 8) b.splice(0, b.length - this.cap);
  }

  override read(channel: string): number[] {
    const b = this.bufs.get(channel) ?? [];
    return b.length > this.cap ? b.slice(b.length - this.cap) : b;
  }

  /** The newest value of a channel, without copying the window. */
  latest(channel: string): number | null {
    const b = this.bufs.get(channel);
    return b && b.length > 0 ? b[b.length - 1] : null;
  }

  override clear(): void {
    this.bufs.clear();
  }
}

/**
 * Route a sensor sample into the scope's channels — EEG/PPG raw per
 * electrode, IMU as acceleration magnitude per hand. Mirrors
 * capture-app/src/live/scope.ts:feedScope.
 */
export function feedScope(scope: Scope, sample: SensorSample): void {
  if (sample.stream.startsWith('eeg:') || sample.stream.startsWith('ppg:')) {
    for (const v of sample.values) scope.push(sample.stream, v);
  } else if (
    (sample.stream === 'accelerometer' ||
      sample.stream === 'linearAcceleration') &&
    sample.values.length >= 3
  ) {
    const [x, y, z] = sample.values;
    scope.push(`accel:${sample.sensorId}`, Math.sqrt(x * x + y * y + z * z));
  }
}
